import { parsePrivacyShortcut } from './privacy';
/** One controller per iframe. Window/origin validation stays with its owner. */
export type GamePauseState = {
  mode: 'cooperative' | 'fallback' | 'unconfirmed';
  paused: boolean;
  gamePaused?: boolean;
  error?: string;
};
export function createGamePauseController(
  post: (data: unknown) => void,
  report: (state: GamePauseState) => void,
) {
  const session = crypto.randomUUID();
  let sequence = 0;
  let latest: {
    type: string;
    version: number;
    session: string;
    sequence: number;
    reasons: string[];
  };
  let timer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;
  return {
    send(privacy: boolean, pageHidden: boolean, shortcut: string) {
      if (disposed) return;
      clearTimeout(timer);
      // Kept for old cached runtimes, including shortcut interception.
      post({
        type: 'MOYU_PRIVACY_CONFIG',
        protocol: 1,
        active: privacy,
        shortcut,
        shortcutBinding: parsePrivacyShortcut(shortcut),
      });
      latest = {
        type: 'MOYU_GAME_CONTROL',
        version: 1,
        session,
        sequence: ++sequence,
        reasons: [...(privacy ? ['privacy'] : []), ...(pageHidden ? ['page-hidden'] : [])],
      };
      const command = latest;
      timer = setTimeout(() => {
        if (disposed || latest !== command) return;
        report({ mode: 'unconfirmed', paused: command.reasons.length > 0 });
        post(command); // One retry; READY and iframe load also resend current state.
      }, 1000);
      post(command);
    },
    receive(data: any) {
      if (
        disposed ||
        !latest ||
        data?.type !== 'MOYU_GAME_STATE' ||
        data.version !== 1 ||
        data.session !== session ||
        data.sequence !== sequence ||
        !['cooperative', 'fallback'].includes(data.mode) ||
        data.paused !== latest.reasons.length > 0 ||
        typeof data.gamePaused !== 'boolean'
      )
        return false;
      clearTimeout(timer);
      report({
        mode: data.mode,
        paused: data.paused,
        gamePaused: data.gamePaused,
        error: data.error === 'adapter-failed' ? data.error : undefined,
      });
      return true;
    },
    dispose() {
      disposed = true;
      clearTimeout(timer);
    },
  };
}
